import { datadogLogs } from '@datadog/browser-logs';
import { JobEntity } from '@shared/db';
import { useMutation } from '@tanstack/react-query';
import { useApiClient } from '@web-app/hooks/useApiClient';
import { END_CALL_LOG_PREFIX } from '@web-app/utils/api/logger-prefixes';

export class EndCallError extends Error {
  public readonly soapNotesError?: string;
  public readonly sessionDetailsError?: string;

  constructor(error: {
    soapNotesError?: string;
    sessionDetailsError?: string;
  }) {
    const message = [error.soapNotesError, error.sessionDetailsError]
      .filter(Boolean)
      .join('; ');
    super(message);
    this.soapNotesError = error.soapNotesError;
    this.sessionDetailsError = error.sessionDetailsError;
  }
}

interface EndCallParams {
  roomId: string;
  duration: number;
  apptOccurredOnThePhone: boolean;
  providerEmail?: string;
  useMockTranscript?: boolean;
}

interface EndCallResponse {
  soapNotes: JobEntity | null;
  sessionDetails: string | null;
  error: {
    soapNotesError?: string;
    sessionDetailsError?: string;
  };
}

export const endCall = async (
  {
    roomId,
    duration,
    apptOccurredOnThePhone,
    providerEmail,
    useMockTranscript,
  }: EndCallParams,
  apiClient: ReturnType<typeof useApiClient>,
): Promise<EndCallResponse> => {
  const safeProviderEmail = providerEmail || 'unknown';

  const [soapNotesResponse, sessionDetailsResponse] = await Promise.allSettled([
    apiClient
      .post(`/daily/soap-notes/generate/${roomId}`, {
        apptOccurredOnThePhone,
        useMockTranscript,
      })
      .then((res) => {
        datadogLogs.logger.info(
          `${END_CALL_LOG_PREFIX} SOAP notes started generating`,
          { roomId, providerEmail: safeProviderEmail },
        );
        return res.data as JobEntity;
      })
      .catch((error) => {
        datadogLogs.logger.error(
          `${END_CALL_LOG_PREFIX} Failed to generate soap notes`,
          {
            roomId,
            providerEmail: safeProviderEmail,
            error: error.message,
            status: error.response?.status,
          },
        );
        throw new Error(`Failed to generate soap notes: ${error.message}`);
      }),

    apiClient
      .post(`/session-details/save/${roomId}`, {
        duration,
        isEndCall: true,
        apptOccurredOnThePhone,
      })
      .then((res) => {
        datadogLogs.logger.info(
          `${END_CALL_LOG_PREFIX} Session details started saving`,
          { roomId, providerEmail: safeProviderEmail },
        );
        return res.data as string;
      })
      .catch((error) => {
        datadogLogs.logger.error(
          `${END_CALL_LOG_PREFIX} Failed to save session details`,
          {
            roomId,
            providerEmail: safeProviderEmail,
            error: error.message,
            status: error.response?.status,
          },
        );
        throw new Error(`Failed to save session details: ${error.message}`);
      }),
  ]);

  const result: EndCallResponse = {
    soapNotes: null,
    sessionDetails: null,
    error: {
      soapNotesError: undefined,
      sessionDetailsError: undefined,
    },
  };

  // Handle SOAP notes result
  if (soapNotesResponse.status === 'fulfilled') {
    result.soapNotes = soapNotesResponse.value;
  } else {
    result.error.soapNotesError =
      soapNotesResponse.reason instanceof Error
        ? soapNotesResponse.reason.message
        : String(soapNotesResponse.reason);
  }

  // Handle session details result
  if (sessionDetailsResponse.status === 'fulfilled') {
    result.sessionDetails = sessionDetailsResponse.value;
  } else {
    result.error.sessionDetailsError =
      sessionDetailsResponse.reason instanceof Error
        ? sessionDetailsResponse.reason.message
        : String(sessionDetailsResponse.reason);
  }

  // Throw error if soap note request failed
  if (result.error.soapNotesError) {
    throw new EndCallError(result.error);
  }

  return result;
};

export const useEndCall = () => {
  const apiClient = useApiClient();

  return useMutation({
    mutationKey: ['berrystreetEndCall'],
    mutationFn: (params: EndCallParams) => endCall(params, apiClient),
    retry: 2,
    retryDelay: 1000,
    onSuccess: (_, variables) => {
      datadogLogs.logger.info(
        `${END_CALL_LOG_PREFIX} End call mutation completed successfully`,
        {
          roomId: variables.roomId,
          duration: variables.duration,
          providerEmail: variables.providerEmail || 'unknown',
          apptOccurredOnThePhone: variables.apptOccurredOnThePhone,
        },
      );
    },
    onError: (
      error,
      { roomId, duration, apptOccurredOnThePhone, providerEmail },
      context,
    ) => {
      const errorPayload = {
        roomId,
        duration,
        apptOccurredOnThePhone,
        providerEmail: providerEmail || 'unknown',
        context,
        timestamp: new Date().toISOString(),
        userAgent: navigator?.userAgent ?? 'unknown',
        error: error instanceof Error ? error.message : String(error),
      };

      datadogLogs.logger.error(
        `${END_CALL_LOG_PREFIX} End call mutation failed`,
        errorPayload,
      );
    },
  });
};
